import { createSelector } from '@reduxjs/toolkit';

import { getExtraBasicInfoQuestions } from '@/constants/tenant-configs';

import { QuestionCode } from '@/types/api/pep';

import { selectOperationData, selectOperationUser } from '../operation';
import { RootState } from '../store';
import { selectConfigsState } from './baseSelectors';

export {
  selectAdobeSignFormFeature,
  selectConfigsState,
  selectFeatureList,
  selectLivenessrecordDisabled,
  selectLivenessSystem,
  selectMailingAddressFeature,
  selectNewApplicationTypeSelectionFeature,
  selectRemediationFeature,
  selectRightStartComingSoonFeature,
  selectRightStartFeature,
  selectIsFeatureInitialized,
} from './baseSelectors';

export const selectConfigsInitialized = createSelector(
  selectConfigsState,
  data => Object.values(data.initialized).every(Boolean),
);

export const selectExistingCustomerConfigs = createSelector(
  (state: RootState) => state.configs,
  data => data.existingCustomer,
);

export const selectUserExtraQuestions = createSelector(
  [selectConfigsState, (state: RootState) => state.auth.user],
  (configs, user) => {
    if (!user) {
      return [];
    }

    const { tenant } = configs;
    const extraBasicInfoQuestions = getExtraBasicInfoQuestions(tenant, user);
    return Object.entries(extraBasicInfoQuestions)
      .filter(([, item]) => item.enabled)
      .map(([questionCode, question]) => ({
        ...question,
        code: questionCode as QuestionCode,
        id: questionCode,
      }))
      .sort((a, b) => a.priority - b.priority);
  },
);
export const selectMinorExtraQuestions = createSelector(
  [selectConfigsState, selectOperationData, selectOperationUser],
  (configs, operationData, minorUser) => {
    if (!minorUser || !operationData.user) {
      return [];
    }

    const { tenant } = configs;
    const extraBasicInfoQuestions = getExtraBasicInfoQuestions(
      tenant,
      minorUser,
    );
    return Object.entries(extraBasicInfoQuestions)
      .filter(([, item]) => item.enabled)
      .map(([questionCode, question]) => ({
        ...question,
        code: questionCode as QuestionCode,
        id: questionCode,
      }))
      .sort((a, b) => a.priority - b.priority);
  },
);

export const selectCanAbandonApplication = createSelector(
  selectConfigsState,
  configs => configs.canAbandonApplication !== false,
);
